import React, { Key } from "react";

import {
  useGetVero1Checklist,
  useStartVero1Checklist,
  isRunInProgressConflict,
} from "manager/hooks/api/vero1Queries";
import Tooltip from "shared/components/Tooltip";

import {
  Vero1ChecklistStatus,
  Vero1GenerationStatus,
} from "shared/interfaces/api/vero1";

import { GhostButton } from "../DealReviewTab/styled";
import { useVero1Viewer } from "../vero1Viewer";

import { useVero1ReviewPanel } from "./context";
import { NO_OUTCOMES_MESSAGE } from "./generationStrings";
import { SpinnerGlyph } from "./icons";
import { TriggerErrorNote, TriggerHold } from "./styled";

export interface Vero1ReviewTriggerProps {
  dealId: Key;
  /** VERO1 must be on for the deal — the API rejects the call otherwise. */
  vero1Active?: boolean;
  /** Deal identity (unit · property) carried into the panel header. */
  dealLabel?: string;
  label?: string;
  /** Show only an existing draft and never generate one — starting a review
   *  stays a deal-page act. */
  resumeOnly?: boolean;
  /** Spec 4.1: the deal review tab re-opens a submitted review from its
   *  header. Elsewhere a submitted review offers nothing. */
  reopen?: boolean;
}

/**
 * Starts the review from the deal banner — the one place a checklist is
 * generated, so the panel always opens on a checklist (spec §6.1). Hold
 * states keep the control visible with copy saying why (spec §01).
 */
export const Vero1ReviewTrigger = ({
  dealId,
  vero1Active,
  dealLabel,
  label,
  resumeOnly = false,
  reopen = false,
}: Vero1ReviewTriggerProps) => {
  const { canAuthor } = useVero1Viewer();
  const {
    openReview,
    closeReview,
    isOpen,
    dealId: openDealId,
    setStartPending,
    setAutoStartWhenReady,
  } = useVero1ReviewPanel();
  const { checklist, generation, isChecklistLoading } = useGetVero1Checklist(
    dealId,
    vero1Active
  );
  const { startChecklist, isStartingChecklist } =
    useStartVero1Checklist(dealId);

  if (!vero1Active || !canAuthor) {
    return null;
  }

  const state = generation?.state;
  // only an OPEN DRAFT is resumed (prototype: draftLive)
  const resuming = checklist?.status === Vero1ChecklistStatus.Draft;
  // a submitted review re-opens from the deal review tab only (spec 4.1)
  const reopening =
    reopen &&
    checklist != null &&
    checklist.status !== Vero1ChecklistStatus.Draft;
  if (checklist?.status === Vero1ChecklistStatus.Submitted && !reopen) {
    return null;
  }

  if (resumeOnly && !resuming) {
    return null;
  }
  if (isOpen && String(openDealId) === String(dealId)) {
    return null;
  }
  // a decisioned deal offers nothing to start, but an open draft stays
  // reachable for discard
  if (state === Vero1GenerationStatus.DealDecisioned && !resuming) {
    return null;
  }

  // an unknown state is not a ready one
  const blocked =
    !resuming && (isChecklistLoading || state !== Vero1GenerationStatus.Ready);
  const noOutcomes =
    !resuming &&
    !isChecklistLoading &&
    state === Vero1GenerationStatus.NoOutcomes;

  const blockedReason = isChecklistLoading
    ? "Checking this deal's screening…"
    : {
        [Vero1GenerationStatus.RunInProgress]:
          "Lumen is still screening this deal. The review can start once screening finishes.",
        // the agent's own click kicked this run, so it reads as their action
        // rather than an external delay they are waiting behind
        [Vero1GenerationStatus.RescreeningForReview]:
          "Re-running screening for the freshest data. The review opens as soon as it lands.",
        [Vero1GenerationStatus.LumenUnavailable]:
          "We couldn't reach screening for this deal. Try again shortly.",
        [Vero1GenerationStatus.NoRun]:
          "Lumen hasn't screened this deal yet. There's nothing to review until screening returns.",
        [Vero1GenerationStatus.NoLumenConfig]:
          "This property has no Lumen configuration yet. Ask an admin to assign a screening formula before a review can start.",
        [Vero1GenerationStatus.DealDecisioned]:
          "This deal has already been decisioned — a review can no longer change the outcome.",
        [Vero1GenerationStatus.NoOutcomes]: NO_OUTCOMES_MESSAGE,
        [Vero1GenerationStatus.ApplicantsPending]:
          "Not every applicant on this deal has submitted, so screening has " +
          "only part of the picture. The review can start once they all submit.",
      }[state as Vero1GenerationStatus] ??
      "This deal isn't ready for review yet.";

  const open = () => openReview(dealId, dealLabel);

  const copy =
    label ??
    (resuming ? "Edit draft review" : reopening ? "Re-open" : "VERO1 review");

  const button = (
    <GhostButton
      type="button"
      data-testid="vero1-review-trigger"
      disabled={blocked || isStartingChecklist}
      onClick={() => {
        if (resuming) {
          open();
          return;
        }
        open();
        setStartPending(true);
        setAutoStartWhenReady(true);
        startChecklist(undefined, {
          onSuccess: () => setAutoStartWhenReady(false),
          onError: (error: unknown) => {
            if (isRunInProgressConflict(error as never)) return;
            setAutoStartWhenReady(false);
            closeReview();
          },
          onSettled: () => setStartPending(false),
        });
      }}
    >
      {isStartingChecklist && <SpinnerGlyph size={12} />}
      {copy}
    </GhostButton>
  );

  if (noOutcomes) {
    return (
      <TriggerHold data-testid="vero1-review-trigger-hold">
        {button}
        <TriggerErrorNote role="alert" data-testid="vero1-review-trigger-error">
          {NO_OUTCOMES_MESSAGE}
        </TriggerErrorNote>
      </TriggerHold>
    );
  }

  return blocked && blockedReason ? (
    <Tooltip title={blockedReason}>
      <span>{button}</span>
    </Tooltip>
  ) : (
    button
  );
};
